import { Subscription } from "../../Types/Generated/LogbookApi/Subscriptions"
import { IStoredCalculationSource } from "../../Types/Generated/LogbookModel/StoredCalculation"
import { entity, type Identified } from "./Connection"

// The generation at/above which a stored calc carries v2 sections. Mirrors
// `StoredCalculation.v2CalculationGeneration` in Sources/LogbookModel.
const v2CalculationGeneration = 2;

// Normalize a raw `StoredCalculation/Log` broadcast (an `IStoredCalculation`: a
// `data` JSON string, no `calculated`) into the stored `IStoredLogCalculation`
// shape the folds read. Version splits the parsed payload into the v1 flat
// (`calculated`) vs v2 sections (`calculatedV2`). Under #1122/#1128 a gen>=2 live
// broadcast already arrives v1-projected (version 1), so the v2 branch is defensive
// plumbing. Guards `data` so deletes (no payload) don't throw and keep their `id`.
function normalizeStoredCalculation(raw: Identified): Identified {
  const r = raw as Identified & { sourceId?: number; sourceType?: number; version?: number; data?: string };
  const parsed = r.data ? JSON.parse(r.data) : undefined;
  const version = r.version ?? 1;
  const aggregateSource =
    r.sourceType === IStoredCalculationSource.cruise ||
    r.sourceType === IStoredCalculationSource.day ||
    r.sourceType === IStoredCalculationSource.trip;
  return {
    id: r.id,
    sourceId: r.sourceId,
    sourceType: r.sourceType,
    version,
    // Day/Trip consumers historically receive the live payload through
    // AppData.applyStoredCalculation, which always updates the compatibility
    // projection and its version. Keep that contract during their read migration.
    calculated: aggregateSource || version < v2CalculationGeneration ? parsed : undefined,
    calculatedV2: !aggregateSource && version >= v2CalculationGeneration ? parsed : undefined,
  } as unknown as Identified;
}

export const StoredCalculationEntity = entity("StoredCalculation", {
  // Each source broadcasts on its own channel. TripView adds day/trip retention
  // (#1612, tracker #665) while preserving the existing log channel.
  live: [
    Subscription.storedCalculationLog,
    Subscription.storedCalculationDay,
    Subscription.storedCalculationTrip,
    Subscription.storedCalculationCruise,
  ],
  normalizeLive: normalizeStoredCalculation,
});
